import {
  Component, EventEmitter, Input, Output, OnChanges, ViewChild, ElementRef,
} from '@angular/core';
import {
  FormBuilder, FormGroup, FormControl,
  Validators, AbstractControl, ValidatorFn
} from '@angular/forms';

import {
  CardapioProdutoDTO,
  CardapioItemPedidoDTO,
  CardapioGrupoModificadorDTO,
} from '../../../../models/cardapio-digital/cardapio-digital.model';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';

/**
 * Tipo de grupo mapeado a partir de CardapioGrupoModificadorDTO.
 * Usa os mesmos nomes do model da referencia (GrupoOpcoes).
 */
type Grupo = {
  titulo: string;
  tipo: 'radio' | 'checkbox' | 'contador';
  obrigatorio?: boolean;
  minEscolha?: number;
  maxEscolha?: number;
  opcoes: Array<{ titulo: string; valor: number; qtdMax?: number }>;
};

@Component({
  selector: 'app-cardapio-modificador-modal',
  templateUrl: './cardapio-modificador-modal.component.html',
  styleUrls: ['./cardapio-modificador-modal.component.scss'],
})
export class CardapioModificadorModalComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() produto: CardapioProdutoDTO | null = null;
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Output() confirmar = new EventEmitter<CardapioItemPedidoDTO>();

  @ViewChild('bodyScroll') bodyScrollRef?: ElementRef<HTMLDivElement>;

  form!: FormGroup;
  cantidad = 1;

  /** Grupos mapeados a partir dos modificadores do produto */
  grupos: Grupo[] = [];

  /** Abre um por vez no accordion */
  activeIndex: number | null = 0;

  /** Snapshot anterior para checkbox limitar pelo max */
  private checkboxPrev: Record<string, any> = {};

  constructor(private fb: FormBuilder, private elementRef: ElementRef) {
    this.form = this.fb.group({
      comentarios: new FormControl<string>('', { nonNullable: true })
    });
  }

  /** Reconstroi o form quando chega novo produto */
  ngOnChanges(): void {
    if (!this.produto) return;
    this.mapearGrupos();
    this.buildForm();
    this.resetUiState();
  }

  /** Ao exibir o dialog, zera tudo e volta pro topo */
  onDialogShow(): void {
    this.resetUiState();
    setTimeout(() => this.scrollBodyTop(), 0);
  }

  /** Ao esconder, limpa e volta ao estado inicial */
  onDialogHide(): void {
    this.resetUiOnly();
    this.visibleChange.emit(false);
  }

  // --- Helpers de UI ---
  private scrollBodyTop(): void {
    const el = this.bodyScrollRef?.nativeElement;
    if (el) el.scrollTop = 0;

    // Resetar tambem o scroll do container do p-dialog (p-dialog-content)
    const dialogContent = this.elementRef.nativeElement.querySelector('.p-dialog-content');
    if (dialogContent) dialogContent.scrollTop = 0;
  }

  private resetUiState(): void {
    this.resetUiOnly();
    this.activeIndex = 0;
  }

  private resetUiOnly(): void {
    this.cantidad = 1;
    if (this.form) {
      this.form.reset();
      this.buildForm();
    }
    this.scrollBodyTop();
  }

  /**
   * Mapeia CardapioGrupoModificadorDTO para o formato Grupo (usado pela referencia).
   * - maximoSelecoes === 1 && minimoSelecoes === 1 -> radio
   * - maximoSelecoes > 1 -> checkbox
   * - Todos os outros -> checkbox
   */
  private mapearGrupos(): void {
    if (!this.produto?.modificadores) {
      this.grupos = [];
      return;
    }

    this.grupos = this.produto.modificadores.map((mod: CardapioGrupoModificadorDTO) => {
      let tipo: 'radio' | 'checkbox' | 'contador';
      if (mod.maximoSelecoes === 1 && mod.minimoSelecoes === 1) {
        tipo = 'radio';
      } else {
        tipo = 'checkbox';
      }

      return {
        titulo: mod.nome,
        tipo,
        obrigatorio: mod.obrigatorio,
        minEscolha: mod.minimoSelecoes,
        maxEscolha: mod.maximoSelecoes,
        opcoes: mod.opcoes.map(o => ({
          titulo: o.nome,
          valor: o.precoAdicional,
        })),
      };
    });
  }

  private buildForm(): void {
    const group: Record<string, any> = {};

    this.grupos.forEach((g: Grupo) => {
      if (g.tipo === 'radio') {
        const defaultVal = g.obrigatorio ? '' : (g.opcoes[0]?.titulo ?? '');
        group[g.titulo] = new FormControl<string>(defaultVal, {
          nonNullable: true,
          validators: g.obrigatorio ? [Validators.required] : []
        });

      } else if (g.tipo === 'checkbox') {
        const inner: Record<string, FormControl<boolean>> = {};
        g.opcoes.forEach(o => inner[o.titulo] = new FormControl<boolean>(false, { nonNullable: true }));
        const fg = this.fb.group(inner, { validators: [this.checkboxSumValidator(g)] });
        group[g.titulo] = fg;

        this.checkboxPrev[g.titulo] = fg.value;
        fg.valueChanges.subscribe(curr => {
          const prev = this.checkboxPrev[g.titulo] ?? {};
          const currCount = Object.values(curr).filter(Boolean).length;
          const max = g.maxEscolha ?? Number.POSITIVE_INFINITY;
          if (currCount > max) {
            fg.patchValue(prev, { emitEvent: false });
            return;
          }
          this.checkboxPrev[g.titulo] = curr;
        });

      } else if (g.tipo === 'contador') {
        const inner: Record<string, FormControl<number>> = {};
        g.opcoes.forEach(o => {
          inner[o.titulo] = new FormControl<number>(0, {
            nonNullable: true,
            validators: [Validators.min(0), Validators.max(o.qtdMax ?? 99)]
          });
        });
        group[g.titulo] = this.fb.group(inner, { validators: [this.counterSumValidator(g)] });
      }
    });

    group['comentarios'] = new FormControl<string>('', { nonNullable: true });
    this.form = this.fb.group(group);
  }

  /* ===== Helpers para o template ===== */
  getControl<T = any>(path: string): FormControl<T> { return this.form.get(path) as FormControl<T>; }
  getGroup(path: string): FormGroup { return this.form.get(path) as FormGroup; }
  get comentarios(): FormControl<string> { return this.getControl<string>('comentarios'); }

  optionHeader(g: Grupo): string {
    return `${g.titulo}${g.obrigatorio ? ' (obligatorio)' : ''}`;
  }

  optionHint(g: Grupo): string {
    const parts: string[] = [];
    if (g.minEscolha && g.minEscolha > 0) parts.push(`al menos ${g.minEscolha}`);
    if (g.maxEscolha && g.maxEscolha > 0) parts.push(`maximo de ${g.maxEscolha}`);
    return parts.length ? `Seleccione ${parts.join(', ')}` : '';
  }

  padId(n?: number): string { return Number(n ?? 0).toString().padStart(4, '0'); }

  trackByGrupo(index: number, g: Grupo): string { return g.titulo; }
  trackByOpcao(index: number, o: { titulo: string; valor: number }): string { return o.titulo; }

  formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  /* ===== Validadores ===== */
  private checkboxSumValidator(g: Grupo): ValidatorFn {
    return (ctrl: AbstractControl) => {
      const vals = ctrl.value || {};
      const count = Object.values(vals).filter(Boolean).length;
      const min = g.minEscolha ?? 0;
      const max = g.maxEscolha ?? Number.POSITIVE_INFINITY;
      if (g.obrigatorio && min === 0 && count < 1) return { minCount: true };
      if (count < min) return { minCount: true };
      if (count > max) return { maxCount: true };
      return null;
    };
  }

  private counterSumValidator(g: Grupo): ValidatorFn {
    return (ctrl: AbstractControl) => {
      const vals = ctrl.value || {};
      const sum = Object.values<number>(vals).reduce((a, b) => a + (Number(b) || 0), 0);
      const min = g.minEscolha ?? 0;
      const max = g.maxEscolha ?? Number.POSITIVE_INFINITY;
      if (g.obrigatorio && min === 0 && sum < 1) return { minCount: true };
      if (sum < min) return { minCount: true };
      if (sum > max) return { maxCount: true };
      return null;
    };
  }

  /* Mensagens de erro */
  checkboxErrorText(g: Grupo): string {
    const min = g.minEscolha ?? (g.obrigatorio ? 1 : 0);
    const max = g.maxEscolha;
    if (max && min) return `Seleccione al menos ${min} y como maximo ${max}.`;
    if (max) return `Seleccione como maximo ${max}.`;
    if (min) return `Seleccione al menos ${min}.`;
    return `Seleccion invalida.`;
  }

  counterErrorText(g: Grupo): string {
    const min = g.minEscolha ?? (g.obrigatorio ? 1 : 0);
    const max = g.maxEscolha;
    if (max && min) return `Seleccione al menos ${min} y como maximo ${max}.`;
    if (max) return `Seleccione como maximo ${max}.`;
    if (min) return `Seleccione al menos ${min}.`;
    return `Cantidad invalida.`;
  }

  /* Desabilita checkbox extra quando atingiu o maximo */
  checkboxDisabled(g: Grupo, key: string): boolean {
    const grp = this.getGroup(g.titulo);
    const vals = grp.value || {};
    const checkedCount = Object.values(vals).filter(Boolean).length;
    const max = g.maxEscolha ?? Number.POSITIVE_INFINITY;
    return checkedCount >= max && !vals[key];
  }

  /* Max dinamico para inputNumber do contador */
  counterMax(g: Grupo, key: string): number {
    const grp = this.getGroup(g.titulo);
    const vals = grp.value || {};
    const current = Number(vals[key] ?? 0);
    const perItemMax = (g.opcoes.find(o => o.titulo === key)?.qtdMax) ?? 99;

    const sumAll = Object.values<number>(vals).reduce((a, b) => a + (Number(b) || 0), 0);
    const groupMax = (g.maxEscolha ?? Infinity);
    const remainingGroup = Math.max(groupMax - (sumAll - current), 0);

    return Math.min(perItemMax, current + remainingGroup);
  }

  /* Pode submeter? */
  get canSubmit(): boolean {
    if (!this.produto) return false;
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    return this.form.valid;
  }

  /* Totais e acoes */
  subtotal(): number {
    if (!this.produto) return 0;
    let total = this.produto.precoPromocional || this.produto.preco;

    this.grupos.forEach((g: Grupo) => {
      if (g.tipo === 'radio') {
        const sel = this.getControl<string>(g.titulo).value;
        if (sel) {
          const opt = g.opcoes.find(o => o.titulo === sel);
          if (opt) total += opt.valor;
        }
      } else if (g.tipo === 'checkbox') {
        const grp = this.getGroup(g.titulo);
        g.opcoes.forEach(o => { if (grp.get(o.titulo)?.value === true) total += o.valor; });
      } else if (g.tipo === 'contador') {
        const grp = this.getGroup(g.titulo);
        g.opcoes.forEach(o => {
          const qtd = Number(grp.get(o.titulo)?.value ?? 0);
          if (qtd > 0) total += o.valor * qtd;
        });
      }
    });

    return total;
  }

  inc(): void { this.cantidad = this.cantidad + 1; }
  dec(): void { this.cantidad = Math.max(1, this.cantidad - 1); }

  /** Avanca para a proxima aba do accordion, se existir */
  private goNext(i: number): void {
    const last = (this.grupos?.length ?? 0) - 1;
    if (i < last) setTimeout(() => this.activeIndex = i + 1, 80);
  }
  onRadioPicked(_g: any, idx: number): void { this.goNext(idx); }
  onCheckboxChanged(g: any, idx: number): void {
    const grp = this.getGroup(g.titulo);
    const selected = g.opcoes.filter((o: any) => grp.get(o.titulo)?.value === true).length;
    const min = Number(g.minEscolha ?? 0);
    const max = Number(g.maxEscolha ?? 0);
    const target = max > 0 ? max : (min > 0 ? min : 1);
    if (selected >= target) this.goNext(idx);
  }
  onCounterChanged(g: any, idx: number): void {
    const grp = this.getGroup(g.titulo);
    const sum = g.opcoes
      .map((o: any) => Number(grp.get(o.titulo)?.value ?? 0))
      .reduce((a: number, b: number) => a + b, 0);
    const min = Number(g.minEscolha ?? 0);
    const max = Number(g.maxEscolha ?? 0);
    const target = max > 0 ? max : (min > 0 ? min : 1);
    if (sum >= target) this.goNext(idx);
  }

  agregarAlPedido(): void {
    if (!this.canSubmit || !this.produto) {
      this.form.markAllAsTouched();
      return;
    }

    const opcoesSelecionadas: CardapioItemPedidoDTO['opcoesSelecionadas'] = [];

    this.grupos.forEach((g: Grupo) => {
      if (g.tipo === 'radio') {
        const sel = this.getControl<string>(g.titulo).value;
        if (sel) {
          const opt = g.opcoes.find(o => o.titulo === sel)!;
          opcoesSelecionadas.push({
            grupoTitulo: g.titulo,
            itens: [{ titulo: opt.titulo, quantidade: 1, valorAdicional: opt.valor }]
          });
        }
      } else if (g.tipo === 'checkbox') {
        const grp = this.getGroup(g.titulo);
        const itens: { titulo: string; quantidade: number; valorAdicional: number }[] = [];
        g.opcoes.forEach(o => {
          if (grp.get(o.titulo)?.value === true) itens.push({ titulo: o.titulo, quantidade: 1, valorAdicional: o.valor });
        });
        if (itens.length) opcoesSelecionadas.push({ grupoTitulo: g.titulo, itens });
      } else if (g.tipo === 'contador') {
        const grp = this.getGroup(g.titulo);
        const itens: { titulo: string; quantidade: number; valorAdicional: number }[] = [];
        g.opcoes.forEach(o => {
          const qtd = Number(grp.get(o.titulo)?.value ?? 0);
          if (qtd > 0) itens.push({ titulo: o.titulo, quantidade: qtd, valorAdicional: o.valor });
        });
        if (itens.length) opcoesSelecionadas.push({ grupoTitulo: g.titulo, itens });
      }
    });

    const item: CardapioItemPedidoDTO = {
      produto: this.produto,
      quantidade: this.cantidad,
      valorTotal: this.subtotal() * this.cantidad,
      opcoesSelecionadas,
      comentarios: this.comentarios.value
    };

    this.confirmar.emit(item);

    // Fecha e reseta tudo
    this.onDialogHide();
  }
}
